// X-Ray Template — Phase 1e four additive components.
//
// 1. StrategicAssessmentDiagram   — 2x2 quadrant panel (renders BELOW Verdict)
// 2. BuilderPathDiagram           — horizontal timeline (renders BELOW Strategic)
// 3. InvestmentTrainingRoadmap    — stacked credential blocks per gap (renders BELOW Builder Path)
// 4. TechnicalCompetencyTable     — 5-col skill table (renders BELOW the three visualisations)
//
// All components honour v4.0 §1.4 framing discipline. Sagicor blue/green accents are
// applied to section dividers; methodology forest palette remains the dominant signal.
import React from "react";
import { Lightbulb } from "lucide-react";

// ── Palette tokens (must mirror main file's `C` constants) ──
const C = {
  forest: "#00583D", forestDark: "#003B28", forestPale: "#E8F0EC",
  forestMid: "#B8D4C7", forestMid2: "#3B7A5C", forestLight: "#00674A",
  mutedRose: "#A85D5D", amberDarker: "#B07E2C", greySoft: "#D8DDE3",
  charcoal: "#1F2933", charcoalSoft: "#4A5568",
  gold: "#C28F2C", goldDark: "#9B6F1E", goldLight: "#F5E6C4", amberSoft: "#FDF6E3",
  ivory: "#FAFAF7", line: "#D8DDE3", lineSoft: "#E8EAEE",
  muted: "#6B7280", mutedLight: "#C5CAD2",
};
const SAGICOR_BLUE = "#00B5EF";
const SAGICOR_GREEN = "#82C341";

const FONT_HEAD = '"Cambria", "Source Serif Pro", Georgia, "Times New Roman", serif';
const FONT_BODY = '"Calibri", "Source Sans Pro", "Segoe UI", system-ui, -apple-system, sans-serif';
const pt = (n) => `${(n * 1.3333).toFixed(2)}px`;

// ── Constants per Phase 1e §10 ──
const PROFICIENCY_DOTS = {
  Proficient: 5,
  Developing: 3,
  Limited: 1,
  Unknown: 0,
};

const GAP_STATUS_PILL = {
  "STRENGTH":          { bg: C.forestPale,                 fg: C.forest,      border: C.forest },
  "ON TRACK":          { bg: C.ivory,                      fg: C.charcoal,    border: C.line },
  "DEVELOP":           { bg: "#FBF1E0",                    fg: C.amberDarker, border: C.gold },
  "CRITICAL GAP":      { bg: "#F8E4E4",                    fg: C.mutedRose,   border: C.mutedRose },
  "DATA UNAVAILABLE":  { bg: "transparent",                fg: C.muted,       border: "transparent" },
};

const INVESTMENT_LEVEL_CHIP = {
  Low:         { bg: C.forestPale, fg: C.forest,      label: "Low" },
  Moderate:    { bg: C.goldLight,  fg: C.goldDark,    label: "Moderate" },
  Significant: { bg: "#F0DAB5",    fg: C.amberDarker, label: "Significant" },
};

// ── Section divider with Sagicor accent ──
function SectionDivider({ label, sectionLetter, icon = null }) {
  return (
    <div
      data-testid={`section-divider-${sectionLetter}`}
      style={{ marginTop: "28px", marginBottom: "14px" }}
    >
      <div style={{ height: 3, background: `linear-gradient(90deg, ${SAGICOR_BLUE} 0%, ${SAGICOR_GREEN} 100%)`, borderRadius: 2 }} />
      <div
        style={{
          marginTop: 10,
          fontFamily: FONT_HEAD,
          fontWeight: 700,
          fontSize: pt(13),
          color: C.forestDark,
          letterSpacing: "0.4px",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        {icon}
        {label}
      </div>
    </div>
  );
}

// ── Proficiency dot indicator (5-dot per Section 8 item 1) ──
function ProficiencyDots({ level }) {
  const filled = PROFICIENCY_DOTS[level] ?? 0;
  const label = level || "Unknown";
  return (
    <span
      data-testid={`proficiency-dots-${level}`}
      aria-label={label}
      title={label}
      style={{ display: "inline-flex", gap: 3, alignItems: "center" }}
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: i < filled ? C.forest : "transparent",
            border: `1px solid ${i < filled ? C.forest : C.mutedLight}`,
            display: "inline-block",
          }}
        />
      ))}
      <span style={{ marginLeft: 6, fontFamily: FONT_BODY, fontSize: pt(8), color: filled === 0 ? C.muted : C.charcoal, fontStyle: filled === 0 ? "italic" : "normal" }}>
        {filled === 0 ? "data unavailable" : label}
      </span>
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════
// COMPONENT 2 — Strategic Assessment Diagram
// ════════════════════════════════════════════════════════════════════
export function StrategicAssessmentDiagram({ data }) {
  const sa = data?.strategic_assessment || {};
  const Q = (label, body, accent) => (
    <div style={{ display: "flex", flexDirection: "column", background: "#FFFFFF", border: `1px solid ${C.line}`, minHeight: 130 }}>
      <div style={{
        background: accent,
        color: "#FFFFFF",
        padding: "6px 12px",
        fontFamily: FONT_HEAD,
        fontWeight: 700,
        fontSize: pt(9),
        letterSpacing: "1px",
        textTransform: "uppercase",
      }}>
        {label}
      </div>
      <div style={{ padding: "10px 12px", fontFamily: FONT_BODY, fontSize: pt(9), lineHeight: 1.45, color: C.charcoal, flex: 1 }}>
        {body || <span style={{ color: C.muted, fontStyle: "italic" }}>Evidence pending</span>}
      </div>
    </div>
  );
  return (
    <div data-testid="section-strategic-assessment">
      <SectionDivider label="Strategic Assessment" sectionLetter="strategic" />
      <div
        style={{
          background: C.forestPale,
          border: `1px solid ${C.line}`,
          padding: 12,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
          gridAutoRows: "1fr",
        }}
      >
        {Q("Current State",     sa.current_state,    C.forest)}
        {Q("Strategic Value",   sa.strategic_value,  C.forest)}
        {Q("Capability Gaps",   sa.capability_gaps,  C.forestMid2)}
        {Q("Recommendation",    sa.recommendation,   C.forestMid2)}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// COMPONENT 3 — Builder Path Diagram
// ════════════════════════════════════════════════════════════════════
export function BuilderPathDiagram({ data }) {
  const bp = data?.builder_path || {};
  const now = bp.now || {};
  const q4 = bp.q4_target || {};

  const anchorStyle = {
    flex: 1,
    minWidth: 0,
    background: "#FFFFFF",
    border: `1px solid ${C.line}`,
    padding: "12px 10px",
    textAlign: "center",
    position: "relative",
  };

  const label = (txt) => (
    <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(9), letterSpacing: 1, textTransform: "uppercase", color: C.forestDark, marginBottom: 8 }}>
      {txt}
    </div>
  );

  const milestoneText = (s) =>
    s ? (
      <div style={{ fontFamily: FONT_BODY, fontSize: pt(8.5), color: C.charcoal, lineHeight: 1.35 }}>{s}</div>
    ) : (
      <div style={{ fontFamily: FONT_BODY, fontSize: pt(8), fontStyle: "italic", color: C.muted }}>milestone pending</div>
    );

  return (
    <div data-testid="section-builder-path">
      <SectionDivider label="Builder Path · 12-Month Progression" sectionLetter="builder-path" />
      <div style={{ position: "relative", padding: "0 0 20px" }}>
        {/* Progression rail behind anchors */}
        <div style={{
          position: "absolute",
          top: 70,
          left: 24, right: 24,
          height: 4,
          background: `linear-gradient(90deg, ${C.gold} 0%, ${C.gold} 30%, ${C.forest} 100%)`,
          borderRadius: 2,
          zIndex: 0,
        }} />
        <div style={{ position: "relative", zIndex: 1, display: "flex", gap: 12 }}>
          <div style={anchorStyle}>
            {label("Now")}
            <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(11), color: C.forestDark }}>{now.tier || "—"}</div>
            <div style={{ fontFamily: FONT_BODY, fontSize: pt(9), color: C.charcoalSoft, marginTop: 4 }}>
              BRI {now.score != null ? `${now.score}/100` : "—"}
            </div>
          </div>
          <div style={anchorStyle}>
            {label("Q1")}
            {milestoneText((bp.q1 || {}).milestone)}
          </div>
          <div style={anchorStyle}>
            {label("Q2")}
            {milestoneText((bp.q2 || {}).milestone)}
          </div>
          <div style={anchorStyle}>
            {label("Q3")}
            {milestoneText((bp.q3 || {}).milestone)}
          </div>
          <div style={{ ...anchorStyle, background: C.forestPale, border: `1.5px solid ${C.forest}` }}>
            {label("Q4 · Target")}
            <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(11), color: C.forestDark }}>{q4.tier || "—"}</div>
            <div style={{ fontFamily: FONT_BODY, fontSize: pt(9), color: C.forestDark, marginTop: 4 }}>
              {q4.score_band || "—"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// COMPONENT 4 — Investment Training Roadmap (stacked credential blocks)
// ════════════════════════════════════════════════════════════════════
export function InvestmentTrainingRoadmap({ data }) {
  const blocks = data?.investment_training_roadmap || [];
  return (
    <div data-testid="section-investment-roadmap">
      <SectionDivider label="Investment Training Roadmap" sectionLetter="investment-roadmap" />
      {blocks.length === 0 ? (
        <div style={{ background: C.forestPale, padding: 16, fontFamily: FONT_BODY, fontSize: pt(9), color: C.muted, fontStyle: "italic", border: `1px solid ${C.line}` }}>
          No named skill gaps in Section D for this candidate.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {blocks.map((b, i) => (
            <div key={i} data-testid={`roadmap-gap-block-${i}`} style={{ border: `1px solid ${C.line}` }}>
              <div style={{
                background: C.forestPale,
                padding: "8px 14px",
                fontFamily: FONT_HEAD,
                fontWeight: 700,
                fontSize: pt(11),
                color: C.forestDark,
              }}>
                {i + 1}. {b.gap_name}
              </div>
              <div style={{ padding: "10px 14px", display: "flex", flexWrap: "wrap", gap: 8 }}>
                {(b.credentials || []).map((c, j) => {
                  const chip = INVESTMENT_LEVEL_CHIP[c.investment_level] || INVESTMENT_LEVEL_CHIP.Moderate;
                  return (
                    <div
                      key={j}
                      data-testid={`roadmap-credential-${i}-${j}`}
                      style={{
                        background: C.ivory,
                        border: `1px solid ${C.forest}`,
                        padding: "8px 12px",
                        minWidth: 200,
                        flex: "1 1 220px",
                        maxWidth: "32%",
                      }}
                    >
                      <div style={{ fontFamily: FONT_HEAD, fontWeight: 700, fontSize: pt(10), color: C.forestDark }}>
                        {c.name}
                      </div>
                      <div style={{ fontFamily: FONT_BODY, fontSize: pt(8.5), color: C.charcoalSoft, marginTop: 2 }}>
                        {c.provider || "—"}
                      </div>
                      <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <span style={{
                          background: chip.bg,
                          color: chip.fg,
                          padding: "2px 8px",
                          borderRadius: 999,
                          fontFamily: FONT_BODY,
                          fontWeight: 700,
                          fontSize: pt(7.5),
                          letterSpacing: 0.4,
                          textTransform: "uppercase",
                        }}>
                          {chip.label} investment
                        </span>
                        <span style={{ fontFamily: FONT_BODY, fontSize: pt(7.5), color: C.muted, textTransform: "uppercase", letterSpacing: 0.4 }}>
                          {c.vehicle_type || "credential"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// COMPONENT 1 — Technical Competency Detail Table
// ════════════════════════════════════════════════════════════════════
export function TechnicalCompetencyTable({ data }) {
  const rows = data?.technical_competency_detail || [];
  return (
    <div data-testid="section-competency-table">
      <SectionDivider label="Technical Competency Detail" sectionLetter="competency-table" />
      {rows.length === 0 ? (
        <div style={{ background: C.forestPale, padding: 16, fontFamily: FONT_BODY, fontSize: pt(9), color: C.muted, fontStyle: "italic", border: `1px solid ${C.line}` }}>
          Methodology note: no layer surfaced skill evidence for this candidate.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_BODY, fontSize: pt(9) }}>
          <thead>
            <tr style={{ background: C.forestPale, textAlign: "left" }}>
              {["Skill", "Proficiency", "Requirement", "Gap Status", "Source"].map((h) => (
                <th key={h} scope="col" style={{
                  padding: "8px 10px",
                  fontFamily: FONT_HEAD,
                  fontWeight: 700,
                  fontSize: pt(9),
                  color: C.forestDark,
                  borderBottom: `2px solid ${C.forest}`,
                  letterSpacing: 0.3,
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const pill = GAP_STATUS_PILL[r.gap_status] || GAP_STATUS_PILL["DATA UNAVAILABLE"];
              const isUnknown = r.gap_status === "DATA UNAVAILABLE";
              return (
                <tr key={i} data-testid={`competency-row-${i}`} style={{ borderBottom: `1px solid ${C.lineSoft}`, background: i % 2 === 0 ? "#FFFFFF" : C.ivory }}>
                  <td style={{ padding: "8px 10px", color: C.charcoal, fontStyle: isUnknown ? "italic" : "normal" }}>
                    {r.skill || "—"}
                  </td>
                  <td style={{ padding: "8px 10px" }}>
                    <ProficiencyDots level={r.proficiency} />
                  </td>
                  <td style={{ padding: "8px 10px", color: C.charcoalSoft, fontStyle: isUnknown ? "italic" : "normal" }}>
                    {r.requirement || "—"}
                  </td>
                  <td style={{ padding: "8px 10px" }}>
                    <span style={{
                      background: pill.bg,
                      color: pill.fg,
                      border: pill.border === "transparent" ? "none" : `1px solid ${pill.border}`,
                      padding: "2px 8px",
                      borderRadius: 999,
                      fontFamily: FONT_BODY,
                      fontWeight: 700,
                      fontSize: pt(7.5),
                      letterSpacing: 0.4,
                      fontStyle: isUnknown ? "italic" : "normal",
                      textTransform: isUnknown ? "lowercase" : "uppercase",
                    }}>
                      {isUnknown ? "data unavailable" : r.gap_status}
                    </span>
                  </td>
                  <td style={{ padding: "8px 10px", color: C.charcoalSoft, fontStyle: isUnknown ? "italic" : "normal", fontFamily: "monospace", fontSize: pt(8) }}>
                    {(r.source_layers && r.source_layers.length) ? r.source_layers.join(" · ") : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}


// ════════════════════════════════════════════════════════════════════
// COMPONENT 5 (Phase 1f) — Recommendation Layer
// ════════════════════════════════════════════════════════════════════
// Fit-score band → bar colour (brief §2)
const FIT_BAR_COLOR = (score, label) => {
  if (label === "Insufficient Evidence" || label === "Pending") return C.line;
  if (score >= 80) return C.forest;
  if (score >= 60) return C.forestMid2;
  if (score >= 40) return C.gold;
  if (score >= 20) return C.amberDarker;
  return C.line;
};

function RecommendationKey() {
  // Always-expanded (Ayo decision: framing reminder must be unmissable).
  const partLabel = (txt) => (
    <div style={{ fontFamily: FONT_HEAD, fontSize: pt(10), color: C.forest, fontWeight: 700, marginTop: 12, marginBottom: 6 }}>
      {txt}
    </div>
  );
  const th = { padding: "5px 8px", background: C.forestMid2, color: "#FFFFFF", textAlign: "left", fontFamily: FONT_BODY, fontWeight: 700, fontSize: pt(8.5), letterSpacing: 0.3 };
  const td = { padding: "5px 8px", background: C.ivory, border: `1px solid ${C.line}`, fontFamily: FONT_BODY, fontSize: pt(9), color: C.charcoal, verticalAlign: "top" };
  return (
    <div data-testid="recommendation-key" style={{ background: C.forestPale, border: `1px solid ${C.line}`, padding: "10px 14px", marginTop: 10 }}>
      <div style={{ fontFamily: FONT_HEAD, fontSize: pt(10.5), color: C.forestDark, fontWeight: 700, letterSpacing: 0.4 }}>Key</div>

      {partLabel("Part 1 — Fit Labels & Score Bands")}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>
          <th style={{ ...th, width: "22%" }}>Label</th>
          <th style={{ ...th, width: "18%" }}>Score Range</th>
          <th style={th}>Meaning</th>
        </tr></thead>
        <tbody>
          {[
            ["Strong Fit", "80-100", "Evidence base aligns strongly with the function's mandate"],
            ["Developing Fit", "60-79", "Evidence base is largely aligned; growth opportunities present"],
            ["Adjacent Fit", "40-59", "Evidence base intersects the function in some areas"],
            ["Limited Fit", "20-39", "Evidence base does not currently align with the function's mandate"],
            ["Not Recommended", "0-19", "Evidence base does not support recommending this function"],
            ["Insufficient Evidence", "n/a", "Re-assessment required before fit can be determined"],
            ["Pending", "n/a", "Awaiting completion of in-progress assessment"],
          ].map(([l, r, m]) => (
            <tr key={l}><td style={td}>{l}</td><td style={td}>{r}</td><td style={td}>{m}</td></tr>
          ))}
        </tbody>
      </table>

      {partLabel("Part 2 — Function Descriptions")}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr><th style={{ ...th, width: "35%" }}>Function</th><th style={th}>What it does</th></tr></thead>
        <tbody>
          {[
            ["Infrastructure & Platforms", "Group-level function led by the Group CTO. Owns cloud, infrastructure, and platform services across Sagicor. Mandate is optimisation and cost discipline."],
            ["Enterprise Architecture & Digital Design", "Group-level function led by the Group Chief Architect. Owns architectural strategy and digital design. The AI Accelerator reports into this function."],
            ["Cyber Security & Technology Risk", "Group-level function led by the Group CISO. Owns cybersecurity posture and technology risk management."],
            ["Business Growth & Digital Enablement", "Business-facing function. Owns technology work that drives revenue, customer experience, and operational outcomes in each business segment."],
          ].map(([n, d]) => <tr key={n}><td style={td}>{n}</td><td style={td}>{d}</td></tr>)}
        </tbody>
      </table>

      {partLabel("Part 3 — What \"Fit\" Means")}
      <div style={{ fontFamily: FONT_BODY, fontSize: pt(9), color: C.charcoal, lineHeight: 1.5 }}>
        The fit score is a directional signal computed from the candidate's skill domain, cadre, tier, and observed technical evidence. A higher score indicates stronger alignment between the candidate's evidence base and the function's mandate. Scores are computed using consistent rules; two candidates with the same evidence inputs receive the same scores. The explanation paragraph below the four rows describes the specific evidence that produced this candidate's top-ranked fit.
      </div>

      {partLabel("Part 4 — Framing Reminder (v4.0 §1.4)")}
      <div style={{ fontFamily: FONT_BODY, fontSize: pt(9), color: C.charcoal, fontStyle: "italic", lineHeight: 1.5 }}>
        This recommendation is workforce planning input, not a placement decision. It surfaces a directional signal that the candidate's manager, regional leadership, and the workforce planning team can build on. No candidate is placed by this recommendation. Workforce decisions are made by Sagicor leadership through their established governance.
      </div>
    </div>
  );
}

export function RecommendationLayer({ data }) {
  const rl = data?.recommendation_layer;
  if (!rl || !Array.isArray(rl.function_fits)) return null;
  const fits = [...rl.function_fits].sort((a, b) => (a.rank || 99) - (b.rank || 99));

  const lightbulb = <Lightbulb size={18} strokeWidth={2} color={C.gold} aria-hidden style={{ flexShrink: 0 }} />;

  return (
    <div data-testid="section-recommendation-layer">
      <SectionDivider label="Recommendation" sectionLetter="recommendation" icon={lightbulb} />
      <div style={{ background: C.ivory, border: `1px solid ${C.line}`, padding: "0 0 14px" }}>
        {/* Header band */}
        <div style={{ background: C.forestPale, padding: "8px 14px", borderBottom: `1px solid ${C.line}`, fontFamily: FONT_HEAD, fontSize: pt(10), letterSpacing: 0.6, textTransform: "uppercase", color: C.forestDark, fontWeight: 700 }}>
          Function-Fit Recommendation
        </div>

        {/* Four function rows */}
        <div style={{ padding: "8px 14px 0" }}>
          {fits.map((f, i) => {
            const isPending = f.fit_label === "Pending";
            const isInsufficient = f.fit_label === "Insufficient Evidence";
            const colour = FIT_BAR_COLOR(f.fit_score, f.fit_label);
            const pct = (isPending || isInsufficient) ? 0 : Math.max(0, Math.min(100, f.fit_score));
            return (
              <div
                key={f.function_name}
                data-testid={`recommendation-row-${i}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(220px, 320px) 1fr 70px 130px",
                  alignItems: "center",
                  gap: 14,
                  padding: "10px 0",
                  borderBottom: i < fits.length - 1 ? `1px solid ${C.line}` : "none",
                }}
              >
                <div>
                  <div style={{ fontFamily: FONT_HEAD, fontSize: pt(11), fontWeight: 700, color: C.forestDark }}>
                    {f.function_name}
                  </div>
                  <div style={{ fontFamily: FONT_BODY, fontSize: pt(8.5), color: C.muted, marginTop: 2 }}>
                    {f.function_lead_title}
                  </div>
                </div>
                <div data-testid={`recommendation-bar-${i}`} style={{ height: 8, background: C.lineSoft, borderRadius: 999, overflow: "hidden" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: colour, borderRadius: 999, transition: "width 250ms ease" }} />
                </div>
                <div style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: pt(10), color: C.charcoal, textAlign: "right", fontWeight: 600 }}>
                  {(isPending || isInsufficient) ? "—" : `${f.fit_score}/100`}
                </div>
                <div style={{ fontFamily: FONT_BODY, fontSize: pt(9), color: colour === C.line ? C.muted : colour, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, textAlign: "right" }}>
                  {f.fit_label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Always-expanded key */}
        <div style={{ padding: "0 14px" }}>
          <RecommendationKey />
        </div>

        {/* Explanation paragraph */}
        {rl.explanation ? (
          <div data-testid="recommendation-explanation" style={{ padding: "12px 14px 0", fontFamily: FONT_BODY, fontSize: pt(10), color: C.charcoal, lineHeight: 1.55 }}>
            {rl.explanation}
          </div>
        ) : null}

        {/* Computation-basis micro footer (audit anchor; non-prose) */}
        {rl.computation_basis ? (
          <div data-testid="recommendation-computation-basis" style={{ padding: "10px 14px 0", fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', fontSize: pt(7.5), color: C.muted, letterSpacing: 0.3 }}>
            inputs: skill_domain={rl.computation_basis.skill_domain_used || "—"} · cadre={rl.computation_basis.cadre_used || "—"} · tier={rl.computation_basis.tier_used || "—"} · succession_track={rl.computation_basis.succession_track || "NA"} · rules_v{rl.computation_basis.rules_version}
          </div>
        ) : null}
      </div>
    </div>
  );
}
